import {
  campaignNameSchema,
  remarksSchema,
  systemNameSchema,
  walletServiceSchema,
} from './campaign';

import {
  BulkGrantAccountTypeKey,
  BulkGrantFileTypeKey,
  ExpiredDatePatternsKey,
} from '@/types';
import {
  InferType,
  string,
  date,
  object,
  number,
} from '@/validations/custom-yup';

/** 付与予定日時 */
export const grantScheduleDateTimeSchema = date()
  .label('付与予定日時')
  .required()
  .transform((value, originalValue) => (
    originalValue instanceof Date
      ? value
      : undefined
  ));

/** 付与予定公開日時 */
export const grantSchedulePublishDateTimeSchema = date()
  .label('付与予定公開日時')
  .required()
  .min(new Date())
  .transform((value, originalValue) => (
    originalValue instanceof Date
      ? value
      : undefined
  ))
  .when(
    'grantScheduleDateTime',
    ([grantScheduleDateTime], schema) => (
      grantScheduleDateTime instanceof Date
        ? schema.test(
          '付与予定公開日時が付与予定日時より前か',
          '付与予定公開日時は付与予定日時より前の日時を指定してください。',
          (value) => (
            value
              ? value.getTime() < grantScheduleDateTime.getTime()
              : true),
        )
        : schema
    ),
  );

/** 付与口座 */
export const bulkGrantAccountTypeSchema = string<BulkGrantAccountTypeKey>()
  .label('付与口座')
  .required(({ label }) => `${label}を選択してください。`)
  .transform((value?: string | { value: BulkGrantAccountTypeKey }) => (typeof value === 'string' ? value : value?.value));

/** 有効期限区分 */
export const expiredDatePatternSchema = string<ExpiredDatePatternsKey>()
  .label('有効期限区分')
  .required(({ label }) => `${label}を選択してください。`);

/** 有効期限（経過日数） */
export const expiredDateAddValueSchema = number()
  .label('有効期限')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'expiredDatePattern',
    ([expiredDatePattern]: ExpiredDatePatternsKey[], schema) => {
      switch (expiredDatePattern) {
        case 'DAYS_LATER':
          return schema
            .required()
            .integer()
            .min(1, ({
              label,
              min,
            }) => `${label}は「${min}日後」以上に設定してください。`)
            .max(1825, ({
              label,
              max,
            }) => `${label}は「${max}日後」以下に設定してください。`)
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .typeError(({ label }) => `${label}は整数で入力してください。`)
            .required();
        case 'MONTHS_LATER_MONTH_END':
        case 'YEARS_LATER_SPECIFIC_MONTH_END':
          return schema
            .required()
            .integer();
        default:
          return schema.notRequired();
      }
    },
  );

/** 有効期限（指定月） */
export const bulkGrantSpecifiedMonthSchema = number()
  .label('有効期限')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'expiredDatePattern',
    ([expiredDatePattern]: ExpiredDatePatternsKey[], schema) => (
      expiredDatePattern === 'YEARS_LATER_SPECIFIC_MONTH_END'
        ? schema.required()
        : schema.notRequired()
    ),
  );

/** 有効期限（指定日） */
export const bulkGrantSpecifiedDateSchema = date()
  .label('有効期限')
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .when(
    'expiredDatePattern',
    ([expiredDatePattern]: ExpiredDatePatternsKey[], schema) => (
      expiredDatePattern === 'SPECIFICATION'
        ? schema.required()
        : schema.notRequired()
    ),
  );

/** ファイル名 */
export const fileNameSchema = string()
  .label('ファイル名')
  .required('CSVファイルをアップロードしてください。');

/** ファイル種別 */
export const fileTypeSchema = string<BulkGrantFileTypeKey>()
  .label('ファイル種別')
  .required(({ label }) => `${label}を選択してください。`);

export const bulkGrantFormSchema = object({
  name: campaignNameSchema,
  systemName: systemNameSchema,
  walletService: walletServiceSchema,
  remarks: remarksSchema,
  fileName: fileNameSchema,
  fileType: fileTypeSchema,
  grantScheduleDateTime: grantScheduleDateTimeSchema,
  grantSchedulePublishDateTime: grantSchedulePublishDateTimeSchema,
  accountType: bulkGrantAccountTypeSchema,
  expiredDatePattern: expiredDatePatternSchema,
  expiredDateAddValue: expiredDateAddValueSchema,
  specifiedMonth: bulkGrantSpecifiedMonthSchema,
  specifiedDate: bulkGrantSpecifiedDateSchema,
})
  .test(
    '付与予定日時より後か',
    () => '有効期限は「付与予定日時」より後に設定してください。',
    ({
      grantScheduleDateTime,
      specifiedDate,
      expiredDatePattern,
    }) => {
      if (!(expiredDatePattern === 'SPECIFICATION' && grantScheduleDateTime && specifiedDate)) return true;

      return grantScheduleDateTime.getTime() < specifiedDate.getTime();
    },
  );

export type BulkGrantForm = InferType<typeof bulkGrantFormSchema>;
